import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { FileText, Eye, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DateCell } from "@/components/DateCell";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PortalQuote {
  id: string;
  quoteNumber: string;
  status: string;
  quoteDate: string;
  expirationDate?: string;
  totalPrice: string;
  account?: { name: string };
}

function getStatusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "accepted":
    case "ACCEPTED":
    case "approved":
      return "default";
    case "sent":
    case "SENT":
    case "pending":
      return "secondary";
    case "rejected":
    case "REJECTED":
    case "expired":
    case "EXPIRED":
      return "destructive";
    default:
      return "outline";
  }
}

function formatCurrency(value: string | number) {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

export default function PortalQuotesPage() {
  const { sessionId } = useAuth();
  const { data, isLoading } = useQuery<{ quotes: PortalQuote[]; total: number }>({
    queryKey: ["/api/portal/quotes"],
    queryFn: () => api.get<{ quotes: PortalQuote[]; total: number }>(`/portal/quotes`, sessionId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const quotes = data?.quotes || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Sales Quotes</h1>
          <p className="text-sm text-muted-foreground">View sales quotes and pricing proposals</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sales Quote History</CardTitle>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No sales quotes found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sales Quote #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-medium">{quote.quoteNumber}</TableCell>
                    <TableCell><DateCell value={quote.quoteDate} /></TableCell>
                    <TableCell><DateCell value={quote.expirationDate} /></TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(quote.status)}>
                        {quote.status.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(quote.totalPrice)}</TableCell>
                    <TableCell>
                      <Link href={`/quotes/${quote.id}`}>
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
